import React, { useEffect, useState } from "react";
import "./App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { ThemeProvider } from "./context/ThemeContext";
import HomePage from "./pages/HomePage";
import WorkPage from "./pages/WorkPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ServicesPage from "./pages/ServicesPage";
import PackagesPage from "./pages/PackagesPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ProjectPage from "./pages/ProjectPage";
import AdminPage from "./pages/AdminPage";
import LoadingScreen from "./components/LoadingScreen";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopOnNavigation from "./components/ScrollToTopOnNavigation";
import PageTransition from "./components/PageTransition";
import { Toaster } from "./components/ui/sonner";

// Animated Routes wrapper for page transitions
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <>
      <ScrollToTopOnNavigation />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
          <Route path="/work" element={<PageTransition><WorkPage /></PageTransition>} />
          <Route path="/work/:projectId" element={<PageTransition><ProjectPage /></PageTransition>} />
          <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
          <Route path="/services" element={<PageTransition><ServicesPage /></PageTransition>} />
          <Route path="/packages" element={<PageTransition><PackagesPage /></PageTransition>} />
          <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
          <Route path="/blog/:postId" element={<PageTransition><BlogPostPage /></PageTransition>} />
          <Route path="/admin" element={<PageTransition><AdminPage /></PageTransition>} />
        </Routes>
      </AnimatePresence>
    </>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Check if user has already seen the loading screen in this session
    const hasSeenLoading = sessionStorage.getItem('bankki-loading-seen');
    if (hasSeenLoading) {
      setIsLoading(false);
      setShowContent(true);
    }
  }, []);

  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const handleLoadingComplete = () => {
    sessionStorage.setItem('bankki-loading-seen', 'true');
    setShowContent(true);
    // Small delay to ensure content is rendered before hiding loading
    setTimeout(() => setIsLoading(false), 50);
  };

  return (
    <HelmetProvider>
      <ThemeProvider>
        {/* Custom Cursor - Disabled for better usability */}
        {/* <CustomCursor /> */}
        
        {/* Scroll to Top Button */}
        <ScrollToTop />
        
        {/* Always render content but hide it during loading */}
        <div 
          className={`App bg-black transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`}
          style={{ visibility: showContent ? 'visible' : 'hidden' }}
        >
          <BrowserRouter>
            <AnimatedRoutes />
            <Toaster />
          </BrowserRouter>
        </div>
        {/* Loading screen on top */}
        {isLoading && (
          <LoadingScreen onLoadingComplete={handleLoadingComplete} />
        )}
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
